import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { GetJobsFilterInput, LibraryFolder } from '@clipr/lib';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../../components/overlays';
import { notifyError, notifySuccess } from '../../services/notifications';
import { assertNotNull, Nullable } from '../../core';
import { JobCatalogSource, JobModel } from '../../entities';
import { CatalogState } from '../../components/layout/catalogState';
import { Error, error, loadError, pageError } from '../../core/error';
import { Team } from '../../entities/team';
import { TeamMemberCatalogSource } from '../../entities/teamMemberCatalogSource';
import debounce from 'lodash/debounce';
import { getApiTeamError, isAgencyAccessRefused } from '../../api';
import type { ApiError } from '../../api/apiError';
import { TagSectionState } from '../../components/tags/tagSectionState';
import { VideoDetailsWindowState } from '../../components/jobs';
import { SearchBarState } from '../../components/search/searchBarState';
import { CombinedReportDrawerState } from '../../components/combinedReportDrawer/combinedReportDrawerState';

type Props = {

}

export type TeamDashboardPageParams = {
  teamId: string
}

export class TeamDashboardPageState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'manageContentOnPageMounted'>(this, {
      isLoading: observable,
      error: observable,
      teamManager: computed,
      teamId: observable,
      team: observable,
      folderParam: observable,
      folderDialog: observable,
      folderName: observable,
      isSavingFolder: observable,
      moveJobIds: observable,
      isMoving: observable,
      canMoveJobs: computed,
      openMoveToFolder: action,
      closeMoveToFolder: action,
      folders: computed,
      folder: computed,
      setFolderParam: action,
      openCreateFolder: action,
      openDeleteFolder: action,
      closeFolderDialog: action,
      setFolderName: action,
      handleTeamMatesModal: action,
      mounted: action,
      manageContentOnPageMounted: action,
      setPagesize: action,
      refreshTeam: action,
      refreshJobs: action,
      setError: action,
      setLoaded: action,
      reset: action,
    });

    this.jobCatalogSource = new JobCatalogSource(this.store, {
      teamId: () => this.teamId,
      pageSize: 20,
      persistSort: true,
      folders: true,
      // A job started inside a folder is listed there, not at the root (and the reverse), until the index catches up.
      jobFilter: (job: JobModel) => job.ownerTeamId === this.teamId
        && (job.folderId ?? null) === this.jobCatalogSource.folderId
    });

    this.combinedReportDrawer = new CombinedReportDrawerState(this.store, this.jobCatalogSource);

    this.teamMemberCatalogSource = new TeamMemberCatalogSource(this.store, {
      teamId: () => this.teamId,
      pageSize: 6
    });

    this.jobCatalog = new CatalogState(this.store, {
      syncStatus: () => this.jobCatalogSource.syncStatus,
      isEndOfList: () => this.jobCatalogSource.isEndOfList
    });
    this.jobCatalog.listen((msg) => {
      switch (msg.type) {
        case 'scrollToBottom':
        case 'loadMore':
          this.jobCatalogSource.fetchMore();
          break;
      }
    });

    this.tagSectionState = new TagSectionState(this.store, {
      teamId: () => this.teamId,
      source: () => this.jobCatalogSource
    });

    this.searchBarState = new SearchBarState(this.store, { teamId: () => this.teamId, searchMode: 'idle' });

    this.folderWindow.listen((msg: Message<WindowState>) => {
      if ((msg.type === 'close' || msg.type === 'outsideClick') && !this.isSavingFolder)
        this.closeFolderDialog();
    });

    this.moveWindow.listen((msg: Message<WindowState>) => {
      if ((msg.type === 'close' || msg.type === 'outsideClick') && !this.isMoving)
        this.closeMoveToFolder();
    });
  }

  isLoading: boolean = true;
  error: Error | null = null;
  readonly searchBarState: SearchBarState;

  get teamManager() {
    return this.store.teamManager;
  }

  teamId: string | null = null;
  team: Team | null = null;

  /** The `?folder=` id from the URL, which may be stale; `folder` is the one that exists. */
  folderParam: string | null = null;
  folderDialog: { type: 'create' } | { type: 'delete', folder: LibraryFolder } | null = null;
  folderName = '';
  isSavingFolder = false;
  readonly folderWindow = new WindowState(this.store);
  /** The jobs the "Move to folder" picker is open for. */
  moveJobIds: string[] | null = null;
  isMoving = false;
  /** The jobs being filed right now, grayed out until the move finishes. */
  readonly movingJobIds = observable.set<string>();
  readonly moveWindow = new WindowState(this.store);

  get folders(): LibraryFolder[] {
    return this.team?.folders ?? [];
  }

  get folder(): LibraryFolder | null {
    return this.folders.find(f => f.id === this.folderParam) ?? null;
  }

  get canDeleteFolders(): boolean {
    return !!this.team?.hasPermission('EditTeam');
  }

  get canMoveJobs(): boolean {
    return !!this.team?.hasPermission('CreateJob') && this.folders.length > 0;
  }

  readonly jobCatalogSource: JobCatalogSource;
  readonly combinedReportDrawer: CombinedReportDrawerState;
  readonly jobCatalog: CatalogState;
  readonly tagSectionState: TagSectionState;

  readonly teamMemberCatalogSource: TeamMemberCatalogSource;

  handleTeamMatesModal() {
    if (this.team?.hasPermission('EditTeam'))
      return this.openTeamMembersEditWindow();

    if (this.team?.hasPermission('ViewTeam'))
      return this.openTeamMembersWindow();

    return false;

  }

  async mounted(params: TeamDashboardPageParams) {
    this.isLoading = true;
    this.searchBarState.reset();
    this.emit('Mounted', { teamId: params.teamId });
    this.bindExternalListeners();
    this.handlePageMountedDebounce(params);
  }

  unmounted() {
    this.store.uploadGuidedTraining.handleTeamDashboardUnmounted(this.teamId);
    this.store.adminGuidedTraining.handleTeamDashboardUnmounted(this.teamId);
    this.reset();
    this.unbindExternalListeners();
    this.emit('Unmounted');
  }

  private async manageContentOnPageMounted(params: TeamDashboardPageParams) {
    const { teamId } = params;
    if (teamId !== this.teamId)
      this.jobCatalogSource.clearSelection();
    this.teamId = teamId;
    this.jobCatalogSource.restoreSort();

    const [, teamErr] = await this.store.teamManager.apiFetchTeam({ id: this.teamId });

    this.setTeam(this.teamManager.getTeam(this.teamId));

    if (!this.team) {
      const [teamRes, teamErr] = await this.store.api.getTeam({ id: this.teamId });

      if (teamErr)
        return this.setTeamError(teamErr);

      this.setTeam(new Team(this.store, teamRes?.getTeam));
    }

    // make sure that none of the requests failed and also we have a Team entity for the requested ID
    if (teamErr || !this.team)
      return this.setTeamError(teamErr);

    this.jobCatalogSource.folderId = this.folder?.id ?? null;

    // we have a team so we can fetch the members, with the same defensive checks
    const [, membersErr] = await this.teamMemberCatalogSource.fetch();

    // const [, invitationsErr] = await team.apiFetchInvitations();
    if (membersErr) // || invitationsErr
      return this.setContentError(membersErr);

    const [, jobsErr] = await this.jobCatalogSource.fetch();
    if (jobsErr)
      return this.setContentError(jobsErr);

    // Fetch the tags for the corresponding team
    await this.tagSectionState.load();

    // everything ok
    this.setLoaded();
    const currentTeamId = this.teamId;
    if (!currentTeamId) return;

    const uploadGuidedTrainingResumed = this.store.uploadGuidedTraining
      .handleTeamDashboardMounted(currentTeamId);
    const adminGuidedTrainingResumed = !uploadGuidedTrainingResumed
      && this.store.adminGuidedTraining.handleTeamDashboardMounted(currentTeamId);

    const createFirstLibraryTrainingBlocks =
      this.store.createFirstLibraryGuidedTraining.shouldBlockNextLevel;
    const showOnboarding = this.store.storage.getLocal(
      'user.showOnboarding',
    ) && !this.store.ui.isMobile && !this.team?.hideUserDetails && !createFirstLibraryTrainingBlocks;

    const canEditTeam = !!this.team?.hasPermission('EditTeam');
    const showAdminTraining = this.store.adminGuidedTraining.canShowForTeam(canEditTeam, this.team);
    const accountGuidedTrainingPayload = {
      showUploadButtons: !!this.team?.hasPermission('CreateJob'),
      showTrainButton: showAdminTraining,
      teamId: currentTeamId,
    };
    const accountGuidedTrainingResumed = !createFirstLibraryTrainingBlocks
      && !uploadGuidedTrainingResumed
      && !adminGuidedTrainingResumed
      && this.store.accountGuidedTraining.resume(accountGuidedTrainingPayload);

    if (!uploadGuidedTrainingResumed && !adminGuidedTrainingResumed && !accountGuidedTrainingResumed && showOnboarding) {
      this.dispatch('openAccountGuidedTraining', accountGuidedTrainingPayload);

      this.store.storage.removeLocal(
        'user.showOnboarding',
      );
    }
  }

  private handlePageMountedDebounce = debounce((params: TeamDashboardPageParams) => this.manageContentOnPageMounted(params), 500);

  setPagesize(pageSize: number) {
    this.jobCatalogSource.setPagesizeValue(pageSize);
  }

  setTeam(team: Nullable<Team>) {
    runInAction(() => {
      this.team = team;
    })
  }

  private bindExternalListeners() {
    const { videoDetailsWindow } = this.store;

    videoDetailsWindow.listen(
      this.videoDetailsWindowListener);
  }

  private videoDetailsWindowListener = action(async (msg: Message<VideoDetailsWindowState>) => {
    switch (msg.type) {
      case 'JobUpdated': {
        this.isLoading = true;
        await this.refreshTeam();
        await this.refreshJobs();
        await this.tagSectionState.load();


        this.isLoading = false;
        break;
      }
    }
  });

  openTeamMembersWindow() {
    assertNotNull(this.teamId);
    const { team } = this;
    if (!team || team.isPublic) {
      // public team
      return;
    }

    this.store.teamMembersWindow.openTeam(this.teamId);
  }

  openTeamMembersEditWindow() {
    assertNotNull(this.teamId);
    const { team } = this;
    if (!team || team.isPublic) {
      // public team
      return;
    }

    this.store.teamMembersWindow.openTeamEdit(this.teamId);
  }

  setFolderParam(id: string | null) {
    this.folderParam = id;
    if (!this.team)
      return; // the first load applies it once the team's folders are known

    this.jobCatalogSource.folderId = this.folder?.id ?? null;
    void this.refreshJobs();
  }

  openCreateFolder() {
    this.folderName = '';
    this.folderDialog = { type: 'create' };
  }

  openDeleteFolder(folder: LibraryFolder) {
    this.folderDialog = { type: 'delete', folder };
  }

  closeFolderDialog() {
    this.folderDialog = null;
  }

  setFolderName(name: string) {
    this.folderName = name;
  }

  async submitFolderDialog() {
    const { team, folderDialog: dialog } = this;
    if (!team || !dialog || this.isSavingFolder)
      return;

    const folderId = dialog.type === 'delete' ? dialog.folder.id : null;
    runInAction(() => { this.isSavingFolder = true; });
    const [res, err] = folderId
      ? await this.store.apiService.deleteLibraryFolder({ teamId: team.id, folderId })
      : await this.store.apiService.createLibraryFolder({ teamId: team.id, name: this.folderName.trim() });

    runInAction(() => {
      this.isSavingFolder = false;
      if (err || !res)
        return notifyError(this, err?.message || 'Could not save the folder.');

      // Team lists reach us through search-index sync, so apply the result locally instead of refetching.
      if ('createLibraryFolder' in res)
        team.setFolders([...this.folders, res.createLibraryFolder].sort((a, b) => a.name.localeCompare(b.name)));
      else
        team.setFolders(this.folders.filter(f => f.id !== folderId));
      this.closeFolderDialog();
    });
    // The deleted folder's items now read as unfiled, so the root list grows.
    if (folderId && !err) void this.refreshJobs();
  }

  /** Moves the selection when the card is part of it, otherwise only that card. */
  openMoveToFolder(jobId: string) {
    const { selectedJobIds } = this.jobCatalogSource;
    this.moveJobIds = selectedJobIds.has(jobId) ? [...selectedJobIds] : [jobId];
  }

  closeMoveToFolder() {
    this.moveJobIds = null;
  }

  /** Files each job into the folder (null unfiles), one call at a time. */
  async moveJobs(jobIds: string[], folderId: string | null) {
    const { team } = this;
    if (!team || this.isMoving || !jobIds.length)
      return;

    runInAction(() => {
      this.isMoving = true;
      jobIds.forEach(id => this.movingJobIds.add(id));
    });
    const moved: string[] = [];
    for (const id of jobIds) {
      // The generated input type has no null, but null is what unfiles a job.
      const [, err] = await this.store.apiService.updateJob({ args: { id, teamId: team.id, folderId: folderId as string } });
      if (!err)
        moved.push(id);
    }

    runInAction(() => {
      this.isMoving = false;
      this.moveJobIds = null;
      this.movingJobIds.clear();
      const { items, selectedJobIds } = this.jobCatalogSource;
      moved.forEach(id => selectedJobIds.delete(id));
      // Drop them locally: the search index lags the write, so a refetch would still list them here.
      items.replace(items.filter(item => !moved.includes(item.jobId)));
    });
    const failed = jobIds.length - moved.length;
    if (failed)
      notifyError(this, `${failed} could not be moved.${moved.length ? ` ${moved.length} moved.` : ''}`);
    else
      notifySuccess(this, `Moved ${moved.length} ${moved.length === 1 ? 'item' : 'items'}.`);
  }

  /** Files every item in the library tagged `tag` into the folder, skipping those already there. */
  async moveTaggedJobs(tag: string, folderId: string) {
    const { team } = this;
    if (!team || this.isMoving)
      return;

    const [tagged, inFolder] = await Promise.all([
      this.fetchJobIds({ teamId: team.id, tags: [tag] }),
      this.fetchJobIds({ teamId: team.id, tags: [tag], folderId }),
    ]);
    if (!tagged || !inFolder)
      return notifyError(this, `Could not load the items tagged #${tag}.`);

    const jobIds = tagged.filter(id => !inFolder.includes(id));
    if (!jobIds.length)
      return notifySuccess(this, `No items tagged #${tag} to move.`);
    await this.moveJobs(jobIds, folderId);
  }

  private async fetchJobIds(filter: GetJobsFilterInput): Promise<string[] | null> {
    const ids: string[] = [];
    let after: string | undefined;
    do {
      const [res, err] = await this.store.apiService.runQuery('getJobs_checkTransients', { filter, first: 100, after });
      if (err || !res)
        return null;
      res.getJobs.edges.forEach(edge => ids.push(edge.node.id));
      after = res.getJobs.pageInfo.hasNextPage ? res.getJobs.pageInfo.endCursor ?? undefined : undefined;
    } while (after);
    return ids;
  }

  async refreshTeam() {
    if (this.teamId)
      await this.store.teamManager.apiFetchTeam({ id: this.teamId });

    this.setTeam(this.teamManager.getTeam(this.teamId));
  }

  async refreshJobs() {
    return await this.jobCatalogSource.fetch();
  }

  setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  setLoaded() {
    this.isLoading = false;
    this.error = null;
  }

  private setTeamError(teamErr: ApiError | null) {
    // A refused library means the loaded profile is stale; refresh it so the sidebar drops it.
    if (isAgencyAccessRefused(teamErr))
      void this.store.agencyOperatorAccess.handleAccessRefused();
    return this.setError(getApiTeamError(teamErr));
  }

  /**
   * A library can still be readable as a public team after its agency access is gone, so the refusal
   * arrives on the members or jobs fetch instead; show it as not found rather than an unknown error.
   */
  private setContentError(err: Error | null) {
    // A dropped connection or a server error is not a permission problem, so don't say it is.
    if (!isAgencyAccessRefused(err))
      return this.setError(loadError('this library'));
    void this.store.agencyOperatorAccess.handleAccessRefused();
    return this.setError(error('NotFound'));
  }

  private unbindExternalListeners() {
    const { store } = this;
    store.videoDetailsWindow.unlisten(
      this.videoDetailsWindowListener);
  }

  reset() {
    this.setTeam(null);
    this.searchBarState.reset();
    this.tagSectionState.reset();
    this.jobCatalogSource.reset();
    this.store.searchService.resetSearchResults();
    this.store.accountGuidedTraining.pause();
    this.store.adminGuidedTraining.pause();
    this.isLoading = false;
    this.error = null;
    this.emit('Unmounted');
  }
}
